import { useEffect, useState, useContext } from "react";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogActions } from "@remo-co/ui-core/src/components/DialogActions";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { DialogTitle } from "@remo-co/ui-core/src/components/DialogTitle";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Select } from "@remo-co/ui-core/src/components/Select";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Loader } from "@remo-co/ui-core/src/components/Loader";
import { useI18n } from "i18n";
import { UserItem } from "modules/participants/types";
import { createEventUserNotification } from "services/firebaseService/eventUserNotifications";
import { MoveParticipant } from "@remo-co/ui-core/src/icons/MoveParticipant";
import { Person } from "@remo-co/ui-core/src/icons/Person";
import { useEventSpaces } from "modules/event/hooks/useEventSpaces";
import { EventUserNotificationType } from "modules/userForceMove/constants";
import { useParticipantLocation } from "modules/participants/hooks";
import { useSelector } from "react-redux";
import { selectUserForceMovePopup } from "modules/userForceMove/redux/selectors";
import { useAppDispatch } from "store/hooks";
import {
  setShowUserForceMoveDialog,
  setDelayThrottleFunction,
} from "modules/userForceMove/redux/slice";
import { selectUser } from "modules/auth/redux/selectors";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { selectCurrentEvent } from "modules/event/selectors";
import { useDelayUserForceMove } from "modules/userForceMove/hooks";
import { selectCurrentSpaceId } from "modules/space/redux/selectors";
import { fetchTableIdForUserInSpace } from "modules/room/userRoom.firebase";
import { getUserName } from "modules/userProfile";
import { selectCurrentRoomId } from "store/rooms";
import { selectCurrentTheaterId } from "modules/theater/selectors";
import { useFloorNames } from "modules/floors/hooks/useFloorNames";
import { DialogCloseButton } from "@remo-co/ui-core/src/components/DialogCloseButton";
import { useStyles } from "./styles";

interface Props {
  user: UserItem;
}

export const UserForceMoveDialog = ({ user }: Props) => {
  const classes = useStyles();
  const { t } = useI18n(["common", "manageEvent", "conversation"]);
  const [floorId, setFloorId] = useState("");
  const [tableId, setTableId] = useState("");
  const {
    spaces,
    tables,
    enableSelection,
    selectedFloorName,
    selectedTableName,
    spacesData,
  } = useEventSpaces({ floorId, tableId });
  const currentUser = useSelector(selectUser);
  const currentFloorId = useSelector(selectCurrentSpaceId);
  const currentTableId = useSelector(selectCurrentRoomId);
  const currentTheaterId = useSelector(selectCurrentTheaterId);
  const floorNames = useFloorNames(currentTheaterId);
  const { findUserInEvent, findUserInFloor } = useParticipantLocation();
  const participantFloorId = findUserInEvent(user.id);
  const [participantTableId, setParticipantTableId] = useState<string | null>(
    null,
  );
  const isPopupEnabled = useSelector(selectUserForceMovePopup);
  const { track } = useContext(TRACKING_CONTEXT);
  const eventData = useSelector(selectCurrentEvent);
  const { moveParticipantDelay } = useDelayUserForceMove();
  const dispatch = useAppDispatch();

  const handleClose = () => {
    setFloorId("");
    setTableId("");
    dispatch(setShowUserForceMoveDialog(undefined));
  };

  useEffect(() => {
    if (!floorId && spaces.length && currentFloorId) setFloorId(currentFloorId);

    if (!tableId && floorId && tables.length) {
      // check if host's current table has a seat, and set that
      if (currentTableId) {
        const currentTableData = tables.find(({ id }) => id === currentTableId);

        if (
          currentTableData &&
          currentTableData.occupiedSeats < currentTableData.capacity &&
          participantTableId !== currentTableId
        ) {
          setTableId(currentTableId);
          return;
        }
      }

      // if not, find the first table with a seat
      const firstTableWithSeats = tables.find(
        ({ occupiedSeats, capacity, id }) =>
          occupiedSeats < capacity && id !== participantTableId,
      );
      if (firstTableWithSeats) {
        setTableId(firstTableWithSeats.id);
      }

      // if no tables have seats in this floor, do not select any table
    }
  }, [
    spaces,
    tables,
    currentFloorId,
    currentTableId,
    tableId,
    floorId,
    participantTableId,
  ]);

  useEffect(() => {
    const getTableIdInFirebase = async () => {
      // not in the same floor - get from Firebase
      if (participantFloorId) {
        const table = await fetchTableIdForUserInSpace(
          participantFloorId,
          user.id,
        );
        setParticipantTableId(table);
      }
    };

    if (participantFloorId === currentFloorId) {
      // participant is in same floor as host, can rely on local state for it
      const table = findUserInFloor(user.id);
      setParticipantTableId(table?.id ?? null);
      return;
    }

    getTableIdInFirebase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantFloorId, currentFloorId, user.id]);

  if (
    !participantFloorId ||
    !participantTableId ||
    isPopupEnabled ||
    !currentUser
  ) {
    return null;
  }

  const handleConfirm = () => {
    moveParticipantDelay();
    dispatch(setDelayThrottleFunction(moveParticipantDelay));
    track(Events.MOVE_USER_CONFIRMED, {
      eventId: eventData?.id,
      userId: user.id,
    });
    createEventUserNotification({
      currentFloorId: participantFloorId,
      targetFloorId: floorId,
      userId: user.id,
      type: EventUserNotificationType.CONFIRM_MOVE,
      targetTableId: tableId,
      currentTableId: participantTableId ?? "",
      floorName: selectedFloorName,
      tableName: selectedTableName,
      eventManagerName: getUserName(currentUser),
      userName: getUserName(user),
    });
    handleClose();
  };

  const handleFloorChange = (selectedFloor: string) => {
    setFloorId(selectedFloor);
    setTableId("");
  };

  const handleTableChange = (selectedTable: string) => {
    setTableId(selectedTable);
  };

  return (
    <Dialog
      open
      PaperProps={{
        classes: {
          root: classes.paperRoot,
        },
      }}
      data-testid="user-move-dialog"
    >
      <DialogContent className={classes.content}>
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.boxTitle}>
            <MoveParticipant className={classes.icon} />
            <Typography variant="h3" className={classes.titleText}>
              {t("conversation:force.move.user", { name: getUserName(user) })}
            </Typography>
          </Box>
          <DialogCloseButton onClick={handleClose} />
        </DialogTitle>
        <Typography variant="body1">
          {t("conversation:force.move.select.table", {
            name: getUserName(user),
          })}
        </Typography>

        <Box className={classes.selectBox}>
          {enableSelection ? (
            <>
              <Select
                inputTestId="select-floor"
                fullWidth
                value={floorId}
                onChange={handleFloorChange}
                id="floor-id"
                options={spaces.map((key, index) => ({
                  value: key,
                  key,
                  label:
                    floorNames[key] ||
                    t("manageEvent:floor.number", {
                      number: index + 1,
                    }),
                  disabled:
                    spacesData[key].occupiedSeats ===
                    spacesData[key].floorCapacity,
                }))}
                label={t("conversation:select.floor")}
              />
              <Select
                inputTestId="select-table"
                fullWidth
                value={tableId}
                id="table-id"
                onChange={handleTableChange}
                options={tables.map(
                  ({ id, name, occupiedSeats, capacity }) => ({
                    value: id,
                    key: id,
                    label: name,
                    disabled:
                      occupiedSeats === capacity ||
                      (participantTableId ? id === participantTableId : false),
                    rightAdornment: (
                      <Box className={classes.rightAdornment}>
                        <Person fontSize="small" />
                        <Typography variant="body1">
                          {occupiedSeats}/{capacity}
                        </Typography>
                      </Box>
                    ),
                  }),
                )}
                optionHeight={350}
                label={t("conversation:select.table")}
              />
              {!eventData?.isOverflowSeatingDisabled && (
                <Typography variant="caption" className={classes.note}>
                  {t("conversation:force.move.overflow.message")}
                </Typography>
              )}
            </>
          ) : (
            <Loader />
          )}
        </Box>

        <DialogActions className={classes.actions}>
          <Button
            variant="secondary"
            color="blue"
            data-testid="confirm-move-button"
            onClick={handleConfirm}
            size="sm"
            disabled={!floorId || !tableId}
          >
            {t("conversation:move.button")}
          </Button>
          <Button
            variant="tertiary"
            data-testid="cancel-button"
            onClick={handleClose}
            size="sm"
          >
            {t("common:button.cancel")}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
